import React, { useState, useMemo } from 'react';
import { useData } from '../../services/DataProvider';
import { Quiz } from '../../../../data-types/Types';
import { useQuizSelectableQuestionCounts } from '../../services/QuizAnalyticsService';
import { useAuth } from '../../services/AuthProvider';
import { Redirect } from 'react-router-dom';
import { IonContent, IonHeader, IonToolbar, IonTitle, IonList, IonItem, IonLabel, IonButton, IonInput, IonCard, IonCardContent, IonCardHeader, IonSpinner, IonNote, IonModal, IonButtons, IonIcon } from '@ionic/react';
import { close, pencil } from 'ionicons/icons';
import { buildQuizTree, buildTopLevelQuizOrder, QuizTreeNode } from '../../utils/quizTree';

const getLastEditedMillis = (value?: unknown) => {
    if (!value) {
        return 0;
    }
    if (value instanceof Date) {
        return value.getTime();
    }
    const maybeTimestamp = value as {
        toMillis?: () => number;
        seconds?: number;
        nanoseconds?: number;
        _seconds?: number;
        _nanoseconds?: number;
    };
    if (typeof maybeTimestamp.toMillis === 'function') {
        return maybeTimestamp.toMillis();
    }
    if (typeof maybeTimestamp.seconds === 'number' || typeof maybeTimestamp._seconds === 'number') {
        const seconds = typeof maybeTimestamp.seconds === 'number'
            ? maybeTimestamp.seconds
            : (maybeTimestamp._seconds as number);
        const nanos = typeof maybeTimestamp.nanoseconds === 'number'
            ? maybeTimestamp.nanoseconds
            : (maybeTimestamp._nanoseconds || 0);
        return seconds * 1000 + Math.floor(nanos / 1e6);
    }
    if (typeof value === 'number') {
        return value;
    }
    if (typeof value === 'string') {
        const parsed = Date.parse(value);
        return Number.isNaN(parsed) ? 0 : parsed;
    }
    return 0;
};

export const QuizManagement: React.FC = () => {
    const { quizzes, adminCreateRecord } = useData();
    const { isAdmin } = useAuth();
    const [editingQuiz, setEditingQuiz] = useState<Quiz | null>(null);
    const [newSubQuizId, setNewSubQuizId] = useState<string>('');

    // Create a memoized map of quiz IDs to names
    const quizNameMap = useMemo(() => {
        const map: Record<string, string> = {};
        quizzes?.forEach(quiz => {
            if (quiz.id) {
                map[quiz.id] = quiz.name;
            }
        });
        return map;
    }, [quizzes]);

    const quizIds = useMemo(
        () => (quizzes ?? []).map((quiz) => quiz.id).filter((id): id is string => !!id),
        [quizzes]
    );
    const quizzesLastEditedMax = useMemo(() => {
        let max = 0;
        (quizzes ?? []).forEach((quiz) => {
            const millis = getLastEditedMillis(quiz.lastEdited);
            if (millis > max) {
                max = millis;
            }
        });
        return max;
    }, [quizzes]);
    const { counts: quizQuestionCountMap, loading: quizCountsLoading } =
        useQuizSelectableQuestionCounts(quizIds, quizzesLastEditedMax);

    const { quizMap, topLevelQuizzes } = useMemo(
        () => buildTopLevelQuizOrder(quizzes || []),
        [quizzes]
    );
    const topLevelTrees = useMemo(
        () => topLevelQuizzes.map((quiz) => buildQuizTree(quizMap, quiz.id || "")).filter((tree): tree is QuizTreeNode => !!tree),
        [topLevelQuizzes, quizMap]
    );

    if (!isAdmin) {
        return <Redirect to="/" />;
    }

    if (!quizzes) {
        return (
            <div className="ion-text-center ion-padding">
                <IonSpinner />
                <p>Loading quizzes...</p>
            </div>
        );
    }

    const handleSave = async (quiz: Quiz) => {
        try {
            await adminCreateRecord('finlitri-quizzes', quiz);
            setEditingQuiz(null);
        } catch (error) {
            console.error('Failed to save quiz:', error);
            alert('Failed to save quiz. Please try again.');
        }
    };

    const handleEdit = (quiz: Quiz) => {
        setEditingQuiz({...quiz});
    };

    const handleCreate = () => {
        const newQuiz: Quiz = {
            name: 'New Quiz',
            subquizIDs: [],
        };
        setEditingQuiz(newQuiz);
    };

    const handleAddSubQuiz = () => {
        if (!editingQuiz || !newSubQuizId.trim()) return;
        
        const updatedQuiz = {
            ...editingQuiz,
            subquizIDs: [...(editingQuiz.subquizIDs || []), newSubQuizId.trim()]
        };
        setEditingQuiz(updatedQuiz);
        setNewSubQuizId('');
    };

    const handleRemoveSubQuiz = (subQuizId: string) => {
        if (!editingQuiz) return;
        
        const updatedQuiz = {
            ...editingQuiz,
            subquizIDs: (editingQuiz.subquizIDs || []).filter(id => id !== subQuizId)
        };
        setEditingQuiz(updatedQuiz);
    };

    const renderQuizNode = (node: QuizTreeNode, depth = 0) => {
        const quizId = node.quiz.id;
        const quizCounts = quizId ? quizQuestionCountMap[quizId] : undefined;
        return (
            <IonItem key={quizId || node.quiz.name} lines="full">
                <IonLabel>
                    <div style={{ paddingLeft: `${depth * 16}px` }}>
                        <div className="ion-text-wrap">
                            <strong>{node.quiz.name}</strong>
                            <IonButton
                                size="small"
                                fill="clear"
                                className="ion-padding-start"
                                onClick={() => handleEdit(node.quiz)}
                                aria-label={`Edit quiz ${node.quiz.name}`}
                                title={`Edit quiz ${node.quiz.name}`}
                            >
                                <IonIcon icon={pencil} />
                            </IonButton>
                            {quizId && (
                                <IonNote className="ion-padding-start">ID: {quizId}</IonNote>
                            )}
                        </div>
                        <div className="ion-text-wrap">
                            Selectable questions:{' '}
                            {quizCountsLoading ? '...' : (quizCounts?.questionCount ?? 0)}
                        </div>
                        <div className="ion-text-wrap">
                            Selectable standards:{' '}
                            {quizCountsLoading ? '...' : (quizCounts?.standardCount ?? 0)}
                        </div>
                        {node.children.length > 0 && (
                            <IonList>
                                {node.children.map((child) => renderQuizNode(child, depth + 1))}
                            </IonList>
                        )}
                    </div>
                </IonLabel>
            </IonItem>
        );
    };

    return (
        <IonContent>
            <IonButton
                expand="block"
                className="ion-margin"
                onClick={handleCreate}
                aria-label="Create new quiz"
                title="Create new quiz"
            >
                Create New Quiz
            </IonButton>

            <IonModal isOpen={!!editingQuiz} onDidDismiss={() => setEditingQuiz(null)}>
                <IonHeader>
                    <IonToolbar>
                        <IonTitle>{editingQuiz?.id ? 'Edit Quiz' : 'New Quiz'}</IonTitle>
                        <IonButtons slot="end">
                            <IonButton
                                onClick={() => setEditingQuiz(null)}
                                aria-label="Close"
                                title="Close"
                            >
                                <IonIcon icon={close} />
                            </IonButton>
                        </IonButtons>
                    </IonToolbar>
                </IonHeader>
                <IonContent>
                    {editingQuiz && (
                        <IonCard className="ion-margin">
                            <IonCardHeader>
                                {editingQuiz.id && (
                                    <IonNote className="ion-padding-start">ID: {editingQuiz.id}</IonNote>
                                )}
                            </IonCardHeader>
                            <IonCardContent>
                                <IonItem>
                                    <IonLabel position="stacked">Name</IonLabel>
                                    <IonInput 
                                        value={editingQuiz.name}
                                        onIonChange={e => setEditingQuiz({
                                            ...editingQuiz, 
                                            name: e.detail.value || ''
                                        })}
                                    />
                                </IonItem>
                                {editingQuiz.id && (
                                    <IonItem lines="none">
                                        <IonLabel>
                                            <p className="ion-text-muted">Selectable questions</p>
                                        </IonLabel>
                                        <IonNote slot="end">
                                            {quizCountsLoading
                                                ? '...'
                                                : (quizQuestionCountMap[editingQuiz.id]?.questionCount ?? 0)}
                                        </IonNote>
                                    </IonItem>
                                )}
                                {editingQuiz.id && (
                                    <IonItem lines="none">
                                        <IonLabel>
                                            <p className="ion-text-muted">Selectable standards</p>
                                        </IonLabel>
                                        <IonNote slot="end">
                                            {quizCountsLoading
                                                ? '...'
                                                : (quizQuestionCountMap[editingQuiz.id]?.standardCount ?? 0)}
                                        </IonNote>
                                    </IonItem>
                                )}

                                <IonItem>
                                    <IonLabel position="stacked">Sub-quizzes</IonLabel>
                                    <div className="ion-padding-vertical">
                                        <IonList>
                                            {(editingQuiz.subquizIDs || []).map((subQuizId) => (
                                                <IonItem key={subQuizId}>
                                                    <IonLabel>
                                                        <h3>{quizNameMap[subQuizId] || 'Unknown Quiz'}</h3>
                                                        <IonNote>ID: {subQuizId}</IonNote>
                                                    </IonLabel>
                                                    <IonButton 
                                                        slot="end"
                                                        fill="clear"
                                                        color="danger"
                                                        onClick={() => handleRemoveSubQuiz(subQuizId)}
                                                        aria-label={`Remove sub-quiz ${quizNameMap[subQuizId] || subQuizId}`}
                                                        title={`Remove sub-quiz ${quizNameMap[subQuizId] || subQuizId}`}
                                                    >
                                                        Remove
                                                    </IonButton>
                                                </IonItem>
                                            ))}
                                        </IonList>
                                    </div>
                                    <div className="ion-margin-top">
                                        <IonInput
                                            placeholder="Add sub-quiz ID"
                                            value={newSubQuizId}
                                            onIonChange={e => setNewSubQuizId(e.detail.value || '')}
                                        />
                                        <IonButton 
                                            size="small"
                                            onClick={handleAddSubQuiz}
                                            disabled={!newSubQuizId.trim()}
                                            aria-label="Add sub-quiz"
                                            title="Add sub-quiz"
                                        >
                                            Add Sub-quiz
                                        </IonButton>
                                    </div>
                                </IonItem>

                                <div className="ion-margin-top">
                                    <IonButton
                                        expand="block"
                                        onClick={() => handleSave(editingQuiz)}
                                        aria-label="Save quiz"
                                        title="Save quiz"
                                    >
                                        Save
                                    </IonButton>
                                    <IonButton
                                        expand="block"
                                        fill="clear"
                                        onClick={() => setEditingQuiz(null)}
                                        aria-label="Cancel edit"
                                        title="Cancel edit"
                                    >
                                        Cancel
                                    </IonButton>
                                </div>
                            </IonCardContent>
                        </IonCard>
                    )}
                </IonContent>
            </IonModal>

            <IonList>
                {topLevelTrees.map((tree) => renderQuizNode(tree))}
            </IonList>
        </IonContent>
    );
};

export default QuizManagement; 